import type { EntitySnapshot } from '../net/protocol';

/**
 * Who wins a click that lands on a loot label. Phase 37, from
 * `doc/grind/design/LOOT-CLICK-AND-PRECEDENCE.md`.
 *
 * **The whole phase is a precedence question**, not a hit-testing one. A label is already
 * drawn above the item and is a far bigger target than the 3D mesh under it — but the labels
 * sit in world space over the play area, and what is usually behind one is the monster the
 * player is fighting. So making them clickable without a rule would trade a fiddly pickup for
 * a swing that silently became a pickup.
 *
 * Everything here is pure and unit-tested. What it decides is *whether a label accepts the
 * pointer at all* — and that is deliberate: a label the rule says should lose is left
 * `pointer-events: none`, so the click reaches the canvas underneath by the browser's own
 * rules rather than by this code re-dispatching it. A re-dispatch would be a second event
 * path that has to stay in step with the first, and the first is Babylon's picking.
 */

/**
 * Whether the player is in melee with the thing they are targeting.
 *
 * **There is no combat state anywhere in this codebase** — no `InCombat` flag, no last-hit
 * timer — and this deliberately does not build one. It is a narrow predicate derived from the
 * snapshot the HUD already holds, which cannot drift out of step with a system that does not
 * exist. The cost is that it says nothing about a fight the player is losing at range; the
 * benefit is that it needs no server change and no new field on the wire.
 *
 * Three things must hold: a target is set, it is alive, and it is inside weapon reach.
 * `reach` is `Derived.attackRange`, which the server sends and which a bow raises — so an
 * archer "in melee" at fourteen units is a correct reading of a character whose weapon
 * reaches that far.
 */
export function engagedInMelee(
  self: EntitySnapshot | undefined,
  target: EntitySnapshot | undefined,
  reach: number,
): boolean {
  if (!self || !target || self.hp <= 0) return false;
  if (target.hp <= 0) return false;
  const dx = target.x - self.x;
  const dz = target.z - self.z;
  return Math.hypot(dx, dz) <= reach;
}

/** What a click on a label would do, or that it should not accept the click at all. */
export type LabelClickVerdict = 'pickup' | 'fall-through';

/**
 * The precedence rule, exactly as the spec states it.
 *
 * - **Alt** → the label always wins. The escape hatch, and the reason it exists: a player
 *   standing in a pack who wants the item they can see needs a way to say so that no
 *   heuristic can override.
 * - **Not engaged** → the label wins. Nothing is being fought, so a click on a name is a
 *   click on the name.
 * - **Engaged in melee** → the monster wins. This is the case the whole rule exists for: a
 *   label drifting over the thing you are hitting must not eat the next swing.
 * - **Hidden by the filter** → not a target at all. A label the filter is not showing is not
 *   on screen to be clicked, and letting an invisible one take a click would make the loot
 *   filter a lie.
 */
export function labelClickVerdict(args: {
  shown: boolean;
  altHeld: boolean;
  engaged: boolean;
}): LabelClickVerdict {
  if (!args.shown) return 'fall-through';
  if (args.altHeld) return 'pickup';
  return args.engaged ? 'fall-through' : 'pickup';
}

/**
 * The CSS value that verdict implies.
 *
 * Separated from the verdict so the rule can be tested without a DOM, and so the one place
 * that turns a decision into a style is obvious. `none` is what lets the click through to
 * Babylon untouched.
 */
export function labelPointerEvents(verdict: LabelClickVerdict): 'auto' | 'none' {
  return verdict === 'pickup' ? 'auto' : 'none';
}
